import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
    RefreshCw, Trash2, ChevronDown, ChevronRight, CheckCircle2, XCircle, Loader2,
    Copy as CopyIcon,
} from 'lucide-react';
import { api, fmtDate, formatApiError } from '@/lib/api';
import { JOBS } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

function StatusBadge({ status }) {
    const map = {
        pending: { label: 'pending', className: 'border-muted-foreground/40 text-muted-foreground' },
        running: { label: 'running', className: 'border-primary/60 text-primary' },
        completed: { label: 'completed', className: 'border-emerald-500/60 text-emerald-500' },
        failed: { label: 'failed', className: 'border-destructive/60 text-destructive' },
    };
    const s = map[status] || map.pending;
    return (
        <Badge variant="outline" className={`rounded-sm ${s.className}`}>
            {s.label}
        </Badge>
    );
}

function ProgressBar({ done, total }) {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
        <div className="w-full">
            <div className="h-1.5 w-full bg-muted rounded-sm overflow-hidden">
                <div
                    className="h-full bg-primary transition-all"
                    style={{ width: `${pct}%` }}
                />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
                {done} / {total} ({pct}%)
            </div>
        </div>
    );
}

export default function JobsPage() {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/copy-jobs');
            setJobs(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Poll while any job is active.
    useEffect(() => {
        const anyActive = jobs.some((j) => j.status === 'pending' || j.status === 'running');
        if (!anyActive) return undefined;
        const t = setInterval(load, 2500);
        return () => clearInterval(t);
    }, [jobs, load]);

    const del = async (id) => {
        try {
            await api.delete(`/copy-jobs/${id}`);
            toast.success('Job removed');
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid={JOBS.root} className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-4xl font-black tracking-tight">Copy Jobs</h1>
                    <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
                        Cross-Space copy operations. Files are copied from a source Space to a destination
                        Space; the original files are left in place. Best-effort — per-file failures are
                        recorded without stopping the batch.
                    </p>
                </div>
                <Button variant="outline" onClick={load} className="rounded-sm">
                    <RefreshCw size={14} className="mr-2" /> Refresh
                </Button>
            </div>

            {loading ? (
                <div className="text-center py-12 text-muted-foreground">Loading jobs…</div>
            ) : jobs.length === 0 ? (
                <div className="rounded-sm border border-dashed border-border p-12 text-center">
                    <div className="font-display text-xl font-bold">No copy jobs yet</div>
                    <p className="text-sm text-muted-foreground mt-2">
                        Open a Space, select files, and click <em>Copy to…</em> to start one.
                    </p>
                </div>
            ) : (
                <div className="rounded-sm border border-border bg-card overflow-x-auto">
                    <table className="w-full text-sm table-tech">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="text-left px-4 py-3 w-8"></th>
                                <th className="text-left px-4 py-3">Source</th>
                                <th className="text-left px-4 py-3">Destination</th>
                                <th className="text-left px-4 py-3">Progress</th>
                                <th className="text-left px-4 py-3">Status</th>
                                <th className="text-left px-4 py-3">Created</th>
                                <th className="text-right px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {jobs.map((j) => (
                                <React.Fragment key={j.id}>
                                    <tr
                                        data-testid={JOBS.row(j.id)}
                                        className="border-b border-border row-hover hover:bg-muted/40"
                                    >
                                        <td className="px-4 py-3">
                                            <button
                                                onClick={() => setExpanded((s) => ({ ...s, [j.id]: !s[j.id] }))}
                                                aria-label="Expand"
                                                className="p-1 rounded-sm hover:bg-muted"
                                            >
                                                {expanded[j.id] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{j.source_space_name}</div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="font-medium">{j.dest_space_name}</div>
                                            {j.dest_prefix && (
                                                <div className="text-xs text-muted-foreground">/ {j.dest_prefix}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 w-56">
                                            <ProgressBar done={j.done} total={j.total} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <StatusBadge status={j.status} />
                                                {j.failed && j.failed.length > 0 && (
                                                    <span className="text-xs text-destructive">
                                                        {j.failed.length} failed
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground">
                                            {fmtDate(j.created_at)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex justify-end">
                                                <AlertDialog>
                                                    <AlertDialogTrigger asChild>
                                                        <Button
                                                            size="icon"
                                                            variant="ghost"
                                                            disabled={j.status === 'pending' || j.status === 'running'}
                                                            data-testid={JOBS.delete(j.id)}
                                                            className="h-7 w-7 rounded-sm text-destructive"
                                                            aria-label="Delete"
                                                        >
                                                            <Trash2 size={13} />
                                                        </Button>
                                                    </AlertDialogTrigger>
                                                    <AlertDialogContent className="rounded-sm border-border">
                                                        <AlertDialogHeader>
                                                            <AlertDialogTitle>Remove job record?</AlertDialogTitle>
                                                            <AlertDialogDescription>
                                                                This only removes the job history. Files already copied are not deleted.
                                                            </AlertDialogDescription>
                                                        </AlertDialogHeader>
                                                        <AlertDialogFooter>
                                                            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                                            <AlertDialogAction
                                                                onClick={() => del(j.id)}
                                                                className="rounded-sm bg-destructive text-destructive-foreground"
                                                            >
                                                                Remove
                                                            </AlertDialogAction>
                                                        </AlertDialogFooter>
                                                    </AlertDialogContent>
                                                </AlertDialog>
                                            </div>
                                        </td>
                                    </tr>
                                    {expanded[j.id] && (
                                        <tr className="bg-muted/20">
                                            <td colSpan={7} className="px-6 py-4">
                                                {j.status === 'completed' && j.failed.length === 0 && (
                                                    <div className="flex items-center gap-2 text-sm text-emerald-500">
                                                        <CheckCircle2 size={14} /> All {j.total} file(s) copied successfully.
                                                    </div>
                                                )}
                                                {j.failed && j.failed.length > 0 && (
                                                    <div>
                                                        <div className="text-sm font-medium text-destructive flex items-center gap-2 mb-2">
                                                            <XCircle size={14} /> {j.failed.length} file(s) failed
                                                        </div>
                                                        <div className="rounded-sm border border-border bg-background max-h-52 overflow-y-auto text-xs font-mono">
                                                            {j.failed.map((f, i) => (
                                                                <div key={i} className="px-3 py-2 border-b border-border last:border-0">
                                                                    <div className="truncate">{f.key}</div>
                                                                    <div className="text-destructive text-[11px] mt-0.5">{f.error}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                {(j.status === 'pending' || j.status === 'running') && (
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        <Loader2 className="animate-spin" size={14} />
                                                        Job in progress…
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
